import { Button } from "@/components/ui/button"
import { searchUsers } from "@/features/profile/services/user.service"
import { useCommentMutations } from "../hooks/useCommentMutations"
import { useComments } from "../hooks/useComments"
import type { CommentableEntity } from "../services/comment.api"
import { CommentForm } from "./CommentForm"
import { CommentItem } from "./CommentItem"

interface CommentListProps {
  entityType: CommentableEntity
  entityId: string
  canComment: boolean
  canReply?: boolean
  canModerate: boolean
  searchQuery?: string
}

export function CommentList({
  entityType,
  entityId,
  canComment,
  canReply = canComment,
  canModerate,
  searchQuery,
}: CommentListProps) {
  const { comments, total, hasNextPage, fetchNextPage, isFetchingNextPage, isLoading, isError } =
    useComments({
      entityType,
      entityId,
      search: searchQuery,
    })
  const mutations = useCommentMutations({ entityType, entityId })

  if (isLoading) {
    return <p className="text-sm text-stone">Loading discussion...</p>
  }

  if (isError) {
    return (
      <div className="rounded-xl border border-red/20 bg-red/5 px-4 py-3 text-sm text-red">
        Failed to load comments. Please try again.
      </div>
    )
  }

  return (
    <div className="pt-2.5 md:pt-5">
      <div className="flex items-center justify-between mb-5 md:mb-6">
        <div className="flex items-center gap-1.25 text-center">
          <span className="text-xl font-semibold text-primary uppercase">Discussion</span>
          <span className="text-[#F2F0EB66] text-[18px] leading-5.5">
            {total > 0 ? ` ${total}` : ""}
          </span>
        </div>
      </div>

      {canComment && (
        <CommentForm
          placeholder="Share a question, reflection, or insight..."
          disabled={mutations.isCreating}
          onSubmit={async content => {
            await mutations.createComment({ content })
          }}
          onSearchUsers={async query => {
            const result = await searchUsers({ search: query, limit: 10 })
            return result.users.map(u => ({
              id: u.id,
              name: u.name || "",
              username: u.username || "",
              profilePictureUrl: u.profilePictureUrl,
            }))
          }}
        />
      )}

      {comments.length === 0 ? (
        <div className="text-sm leading-6.25 text-primary mt-5 md:mt-7.5">
          {searchQuery
            ? "No discussions match your search."
            : "Nothing here yet. Be the first to share your thoughts."}
        </div>
      ) : (
        <div className="space-y-7.5 mt-7.5">
          {comments.map(comment => (
            <CommentItem
              key={comment.id}
              comment={comment}
              canReply={canReply}
              canModerate={canModerate}
              onReply={async (commentId, content) => {
                await mutations.createComment({ content, parentId: commentId })
              }}
              onEdit={async (commentId, content) => {
                await mutations.updateComment({ commentId, params: { content } })
              }}
              onDelete={async commentId => {
                await mutations.deleteComment(commentId)
              }}
              onTogglePin={async commentId => {
                await mutations.togglePinComment(commentId)
              }}
            />
          ))}
        </div>
      )}

      {hasNextPage && (
        <Button
          variant="outline"
          className="w-full"
          onClick={() => void fetchNextPage()}
          disabled={isFetchingNextPage}
        >
          {isFetchingNextPage ? "Loading..." : "Load more"}
        </Button>
      )}
    </div>
  )
}
